import { useCallback, useMemo, useState } from 'react';
import { attendanceAPI } from '../../../services/api';
import { toast } from 'react-toastify';
import { calculateOvertimeMs, normalizeAssignedShift, OVERTIME_REMARKS_THRESHOLD_MS } from '../utils/attendanceUtils';

const HISTORY_PAGE_SIZE = 20;
const requiresRemarks = ({ checkInTime, checkOutTime, remarks, assignedShift }) => {
  const overtimeMs = calculateOvertimeMs(checkInTime, checkOutTime, assignedShift);
  return overtimeMs > OVERTIME_REMARKS_THRESHOLD_MS && !remarks.trim();
};

const getTodayLocalDate = () => {
  const now = new Date();
  const local = new Date(now.getTime() - now.getTimezoneOffset() * 60000);
  return local.toISOString().slice(0, 10);
};

export const useAttendance = (role) => {
  const [history, setHistory] = useState([]);
  const [todayRecord, setTodayRecord] = useState(null);
  const [remarks, setRemarks] = useState('');
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState('');
  const [pendingDeleteId, setPendingDeleteId] = useState(null);
  const [adminUsernameFilter, setAdminUsernameFilter] = useState('');
  const [showFlaggedOnly, setShowFlaggedOnly] = useState(false);
  const [missedCheckoutRequests, setMissedCheckoutRequests] = useState([]);
  const [absenceType, setAbsenceType] = useState('sick-leave');
  const [absenceReason, setAbsenceReason] = useState('');
  const [vacationStartDate, setVacationStartDate] = useState(getTodayLocalDate());
  const [vacationEndDate, setVacationEndDate] = useState(getTodayLocalDate());
  const [missedCheckoutAttendanceId, setMissedCheckoutAttendanceId] = useState('');
  const [missedCheckoutTime, setMissedCheckoutTime] = useState('');
  const [missedCheckoutReason, setMissedCheckoutReason] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [page, setPage] = useState(1);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: HISTORY_PAGE_SIZE,
    total: 0,
    totalPages: 1
  });
  const [editingRecordId, setEditingRecordId] = useState('');
  const [editStatus, setEditStatus] = useState('checked-in');
  const [editAssignedShift, setEditAssignedShift] = useState('');
  const [editRemarks, setEditRemarks] = useState('');
  const [editCheckInTime, setEditCheckInTime] = useState('');
  const [editCheckOutTime, setEditCheckOutTime] = useState('');
  const [activeRemark, setActiveRemark] = useState('');

  const toLocalDateTimeInput = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const local = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return local.toISOString().slice(0, 16);
  };

  const loadData = useCallback(async (options = {}) => {
    try {
      setLoading(true);
      setError('');

      const effectivePage = options.pageOverride || page;
      const effectiveStartDate = Object.prototype.hasOwnProperty.call(options, 'startDate')
        ? options.startDate
        : startDate;
      const effectiveEndDate = Object.prototype.hasOwnProperty.call(options, 'endDate')
        ? options.endDate
        : endDate;
      const effectiveFlaggedOnly = Object.prototype.hasOwnProperty.call(options, 'flaggedOnly')
        ? options.flaggedOnly
        : showFlaggedOnly;

      const [todayRes, historyRes, requestsRes] = await Promise.all([
        attendanceAPI.getToday(),
        attendanceAPI.getHistory({
          page: effectivePage,
          limit: HISTORY_PAGE_SIZE,
          ...(effectiveStartDate ? { startDate: effectiveStartDate } : {}),
          ...(effectiveEndDate ? { endDate: effectiveEndDate } : {}),
          ...(role === 'admin' && effectiveFlaggedOnly ? { flagged: true } : {}),
          ...(role === 'admin' && adminUsernameFilter
            ? { username: adminUsernameFilter.trim() }
            : {})
        }),
        role === 'admin'
          ? attendanceAPI.getMissedCheckoutRequests({ status: 'pending' })
          : Promise.resolve({ data: { requests: [] } })
      ]);

      setTodayRecord(todayRes.data?.record || null);
      setHistory(historyRes.data?.records || []);
      setPagination(historyRes.data?.pagination || {
        page: effectivePage,
        limit: HISTORY_PAGE_SIZE,
        total: 0,
        totalPages: 1
      });
      setMissedCheckoutRequests(requestsRes.data?.requests || []);
      setPage(historyRes.data?.pagination?.page || effectivePage);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load attendance data.');
    } finally {
      setLoading(false);
    }
  }, [role, adminUsernameFilter, showFlaggedOnly, page, startDate, endDate]);

  const openRemarkViewer = useCallback((value) => {
    setActiveRemark(String(value || ''));
  }, []);

  const closeRemarkViewer = useCallback(() => {
    setActiveRemark('');
  }, []);

  const checkIn = useCallback(async () => {
    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.checkIn({ remarks: remarks.trim() });
      toast.success('Checked in successfully.');
      setRemarks('');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to check in.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [remarks, loadData]);

  const checkOut = useCallback(async () => {
    try {
      if (requiresRemarks({
        checkInTime: todayRecord?.checkInTime,
        checkOutTime: new Date(),
        remarks,
        assignedShift: todayRecord?.assignedShift
      })) {
        const message = 'Remarks are required when overtime exceeds 1 hour (more than 9 hours worked).';
        setError(message);
        toast.error(message);
        return;
      }

      setActionLoading(true);
      setError('');
      await attendanceAPI.checkOut({ remarks: remarks.trim() });
      toast.success('Checked out successfully.');
      setRemarks('');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to check out.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [todayRecord, remarks, loadData]);

  const reportAbsence = useCallback(async () => {
    const safeReason = absenceReason.trim();
    if (safeReason.length < 3) {
      toast.error('Please provide a reason (minimum 3 characters).');
      return false;
    }

    const isVacation = absenceType === 'vacation';
    if (isVacation) {
      if (!vacationStartDate || !vacationEndDate) {
        toast.error('Vacation requires both start and end dates.');
        return false;
      }
      if (vacationEndDate < vacationStartDate) {
        toast.error('Vacation end date must be the same as or after start date.');
        return false;
      }
    }

    try {
      setActionLoading(true);
      setError('');
      const payload = {
        absenceType,
        reason: safeReason
      };
      if (isVacation) {
        payload.startDate = vacationStartDate;
        payload.endDate = vacationEndDate;
      }

      await attendanceAPI.reportAbsence(payload);
      toast.success(isVacation ? 'Vacation timeline reported successfully.' : 'Absence reported successfully.');
      setAbsenceReason('');
      await loadData();
      return true;
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to report absence.';
      setError(message);
      toast.error(message);
      return false;
    } finally {
      setActionLoading(false);
    }
  }, [absenceType, absenceReason, vacationStartDate, vacationEndDate, loadData]);

  const goToPage = useCallback(async (targetPage) => {
    const safePage = Math.max(1, targetPage);
    await loadData({ pageOverride: safePage });
  }, [loadData]);

  const applyAdminFilter = useCallback(async () => {
    setPage(1);
    await loadData({ pageOverride: 1 });
  }, [loadData]);

  const clearFilters = useCallback(async () => {
    setAdminUsernameFilter('');
    setShowFlaggedOnly(false);
    setStartDate('');
    setEndDate('');
    setPage(1);
    await loadData({ pageOverride: 1, startDate: '', endDate: '' });
  }, [loadData]);

  const toggleFlaggedOnly = useCallback(async () => {
    const next = !showFlaggedOnly;
    setShowFlaggedOnly(next);
    setPage(1);
    await loadData({ pageOverride: 1, flaggedOnly: next });
  }, [showFlaggedOnly, loadData]);

  const submitMissedCheckoutRequest = useCallback(async () => {
    if (!missedCheckoutAttendanceId) {
      toast.error('Please select an attendance record.');
      return;
    }
    if (!missedCheckoutTime) {
      toast.error('Please select the requested check-out time.');
      return;
    }
    if (missedCheckoutReason.trim().length < 3) {
      toast.error('Please provide a reason (minimum 3 characters).');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.submitMissedCheckoutRequest({
        attendanceId: missedCheckoutAttendanceId,
        requestedCheckOutTime: new Date(missedCheckoutTime).toISOString(),
        reason: missedCheckoutReason.trim()
      });
      toast.success('Missed-checkout request submitted.');
      setMissedCheckoutAttendanceId('');
      setMissedCheckoutTime('');
      setMissedCheckoutReason('');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to submit missed-checkout request.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [missedCheckoutAttendanceId, missedCheckoutTime, missedCheckoutReason, loadData]);

  const approveMissedCheckoutRequest = useCallback(async (requestId) => {
    if (!requestId) return;
    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.approveMissedCheckoutRequest(requestId);
      toast.success('Missed-checkout request approved.');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to approve request.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [loadData]);

  const rejectMissedCheckoutRequest = useCallback(async (requestId) => {
    if (!requestId) return;
    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.rejectMissedCheckoutRequest(requestId);
      toast.success('Missed-checkout request rejected.');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to reject request.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [loadData]);

  const resolveVisibleFlags = useCallback(async () => {
    if (role !== 'admin') return;

    const flaggedIds = history
      .filter((item) => item.flaggedForReview)
      .map((item) => item._id)
      .filter(Boolean);

    if (flaggedIds.length === 0) {
      toast.info('No flagged records on this page.');
      return;
    }

    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.resolveReviewFlagsBulk({
        attendanceIds: flaggedIds,
        reviewNote: 'Resolved by admin after adjustment review.'
      });
      toast.success('Flagged records resolved for this page.');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to resolve flagged records.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [role, history, loadData]);

  const applyQuickRange = useCallback(async (rangeKey) => {
    const today = new Date();
    const todayStr = today.toISOString().slice(0, 10);

    let nextStartDate = '';
    let nextEndDate = '';

    if (rangeKey === 'today') {
      nextStartDate = todayStr;
      nextEndDate = todayStr;
    } else if (rangeKey === 'last7') {
      const start = new Date(today);
      start.setDate(start.getDate() - 6);
      nextStartDate = start.toISOString().slice(0, 10);
      nextEndDate = todayStr;
    } else if (rangeKey === 'month') {
      const start = new Date(today.getFullYear(), today.getMonth(), 1);
      nextStartDate = start.toISOString().slice(0, 10);
      nextEndDate = todayStr;
    }

    setStartDate(nextStartDate);
    setEndDate(nextEndDate);
    setPage(1);
    await loadData({ pageOverride: 1, startDate: nextStartDate, endDate: nextEndDate });
  }, [loadData]);

  const startAdminEdit = useCallback((record) => {
    setEditingRecordId(record._id);
    setEditStatus(record.status || 'checked-in');
    setEditAssignedShift(normalizeAssignedShift(record.assignedShift) || '');
    setEditRemarks(record.remarks || '');
    setEditCheckInTime(toLocalDateTimeInput(record.checkInTime));
    setEditCheckOutTime(toLocalDateTimeInput(record.checkOutTime));
  }, []);

  const cancelAdminEdit = useCallback(() => {
    setEditingRecordId('');
    setEditStatus('checked-in');
    setEditAssignedShift('');
    setEditRemarks('');
    setEditCheckInTime('');
    setEditCheckOutTime('');
  }, []);

  const saveAdminEdit = useCallback(async () => {
    if (!editingRecordId) return;

    try {
      setActionLoading(true);
      setError('');
      const payload = {
        remarks: editRemarks,
        status: editStatus
      };

      if (normalizeAssignedShift(editAssignedShift)) {
        payload.assignedShift = normalizeAssignedShift(editAssignedShift);
      }

      if (editCheckInTime) {
        const parsedCheckIn = new Date(editCheckInTime);
        if (Number.isNaN(parsedCheckIn.getTime())) {
          toast.error('Invalid check-in time format.');
          setActionLoading(false);
          return;
        }
        payload.checkInTime = parsedCheckIn.toISOString();
      }

      if (editCheckOutTime) {
        const parsedCheckOut = new Date(editCheckOutTime);
        if (Number.isNaN(parsedCheckOut.getTime())) {
          toast.error('Invalid check-out time format.');
          setActionLoading(false);
          return;
        }
        payload.checkOutTime = parsedCheckOut.toISOString();
      }

      if (payload.checkInTime && payload.checkOutTime) {
        const checkInMs = new Date(payload.checkInTime).getTime();
        const checkOutMs = new Date(payload.checkOutTime).getTime();
        if (checkOutMs < checkInMs) {
          toast.error('Check-out time cannot be earlier than check-in time.');
          setActionLoading(false);
          return;
        }
      }

      const effectiveCheckIn = payload.checkInTime || editCheckInTime;
      const effectiveCheckOut = payload.checkOutTime || editCheckOutTime;
      const effectiveAssignedShift = payload.assignedShift || normalizeAssignedShift(editAssignedShift);
      if (requiresRemarks({
        checkInTime: effectiveCheckIn,
        checkOutTime: effectiveCheckOut,
        remarks: editRemarks,
        assignedShift: effectiveAssignedShift
      })) {
        const message = 'Remarks are required when overtime exceeds 1 hour (more than 9 hours worked).';
        setError(message);
        toast.error(message);
        setActionLoading(false);
        return;
      }

      await attendanceAPI.updateAttendance(editingRecordId, payload);
      toast.success('Attendance record updated.');
      cancelAdminEdit();
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to update attendance record.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [editingRecordId, editRemarks, editStatus, editAssignedShift, editCheckInTime, editCheckOutTime, cancelAdminEdit, loadData]);

  // Deletion is confirmed by a themed dialog on the page; this only records
  // which record is awaiting confirmation.
  const adminDeleteRecord = useCallback((recordId) => setPendingDeleteId(recordId), []);

  const cancelAdminDelete = useCallback(() => setPendingDeleteId(null), []);

  const confirmAdminDelete = useCallback(async () => {
    if (!pendingDeleteId) return;

    try {
      setActionLoading(true);
      setError('');
      await attendanceAPI.deleteAttendance(pendingDeleteId);
      toast.success('Attendance record deleted.');
      await loadData();
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to delete attendance record.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
      setPendingDeleteId(null);
    }
  }, [pendingDeleteId, loadData]);

  const exportCsv = useCallback(async (mode = 'all') => {
    try {
      setActionLoading(true);
      setError('');

      const response = await attendanceAPI.exportHistoryCsv({
        mode,
        ...(mode === 'page' ? { page, limit: HISTORY_PAGE_SIZE } : {}),
        ...(startDate ? { startDate } : {}),
        ...(endDate ? { endDate } : {}),
        ...(role === 'admin' && adminUsernameFilter ? { username: adminUsernameFilter.trim() } : {})
      });

      const blob = new Blob([response.data], { type: 'text/csv;charset=utf-8' });
      const objectUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = objectUrl;
      const disposition = response.headers?.['content-disposition'] || '';
      const fileNameMatch = disposition.match(/filename="?([^"]+)"?/i);
      link.download = fileNameMatch?.[1] || `attendance_export_${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(objectUrl);

      toast.success('Attendance CSV exported.');
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to export attendance CSV.';
      setError(message);
      toast.error(message);
    } finally {
      setActionLoading(false);
    }
  }, [role, page, startDate, endDate, adminUsernameFilter]);

  const attendanceStateLabel = useMemo(() => {
    if (!todayRecord) return 'Not checked in yet';
    if (todayRecord.attendanceKind === 'absence') {
      if (todayRecord.absenceType === 'sick-leave') return 'Absent (Sick leave)';
      if (todayRecord.absenceType === 'vacation') return 'Absent (Vacation)';
      return 'Absent';
    }
    if (todayRecord.status === 'checked-out') return 'Checked out';
    return 'Checked in';
  }, [todayRecord]);

  return {
    history,
    todayRecord,
    remarks,
    loading,
    actionLoading,
    error,
    adminUsernameFilter,
    showFlaggedOnly,
    missedCheckoutRequests,
    absenceType,
    absenceReason,
    vacationStartDate,
    vacationEndDate,
    missedCheckoutAttendanceId,
    missedCheckoutTime,
    missedCheckoutReason,
    startDate,
    endDate,
    page,
    pagination,
    editingRecordId,
    editStatus,
    editAssignedShift,
    editRemarks,
    editCheckInTime,
    editCheckOutTime,
    activeRemark,
    attendanceStateLabel,
    setRemarks,
    setAdminUsernameFilter,
    setAbsenceType,
    setAbsenceReason,
    setVacationStartDate,
    setVacationEndDate,
    setMissedCheckoutAttendanceId,
    setMissedCheckoutTime,
    setMissedCheckoutReason,
    setStartDate,
    setEndDate,
    setEditStatus,
    setEditAssignedShift,
    setEditRemarks,
    setEditCheckInTime,
    setEditCheckOutTime,
    openRemarkViewer,
    closeRemarkViewer,
    loadData,
    checkIn,
    checkOut,
    reportAbsence,
    goToPage,
    applyAdminFilter,
    clearFilters,
    toggleFlaggedOnly,
    submitMissedCheckoutRequest,
    approveMissedCheckoutRequest,
    rejectMissedCheckoutRequest,
    resolveVisibleFlags,
    applyQuickRange,
    startAdminEdit,
    cancelAdminEdit,
    saveAdminEdit,
    adminDeleteRecord,
    pendingDeleteId,
    confirmAdminDelete,
    cancelAdminDelete,
    exportCsv
  };
};
